/**
 * FamilyPage — The Family Room. The people hub of the Residence.
 *
 * First section: the people, as an editorial member grid. Each card is a
 * calm summary drawn from central records (schedule, tasks, goals) and
 * opens that member's personal space. Below: genuinely shared, persisted
 * family information only — shared goals and the family charter.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FamilyMemberEditModal } from "./components/FamilyMemberEditModal";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { usePersistentState } from "./state/usePersistentState";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  TextLink, PillButton, Caption, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";
import { identityLine } from "./utils/memberIntelligence";
import { fmtClock } from "./components/TodayBriefSections";

const GOAL_STATUS = { not_started: "Not started", in_progress: "In progress", complete: "Complete" };
const softText = { color: "var(--ara-ink-soft)" };
const inkRow = { color: "var(--ara-ink)", fontFamily: "Inter, system-ui, sans-serif", fontWeight: 500 };

function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function MemberCard({ member, nextEvent, outstanding, activeGoal, onOpen }) {
  const firstName = (member.preferredName || member.name || "").trim().split(" ")[0];
  return (
    <div className="py-6" data-testid={`family-card-${member.id}`}>
      <p
        className="font-display text-[22px] leading-tight tracking-[-0.01em]"
        style={{ color: "var(--ara-ink)", fontWeight: 400 }}
      >
        {member.name}
      </p>
      <p className="mt-1 caption-ara" style={softText}>{identityLine(member) || member.role || "Household member"}</p>

      <div className="mt-4 space-y-2">
        {nextEvent && (
          <p className="text-[13.5px] leading-snug" style={inkRow} data-testid={`family-card-next-event-${member.id}`}>
            <span className="tabular-nums" style={{ ...softText, fontWeight: 400 }}>{fmtClock(nextEvent.startTime)}</span>
            {"  "}{nextEvent.title}
          </p>
        )}
        <p className="text-[13.5px] leading-snug" style={outstanding > 0 ? inkRow : { ...inkRow, ...softText, fontWeight: 400 }} data-testid={`family-card-outstanding-${member.id}`}>
          {outstanding === 0 ? "Nothing outstanding" : `${outstanding} thing${outstanding === 1 ? "" : "s"} to do`}
        </p>
        {activeGoal && (
          <p className="text-[13.5px] leading-snug italic" style={{ ...softText, fontFamily: "var(--ara-font-display), serif" }} data-testid={`family-card-goal-${member.id}`}>
            {activeGoal.title}
          </p>
        )}
      </div>

      <div className="mt-5">
        <TextLink onClick={onOpen} testid={`open-space-${member.id}`}>
          Open {firstName}'s Space →
        </TextLink>
      </div>
    </div>
  );
}

export default function FamilyPage() {
  const { members, tasks, goals, dailyFamilySchedule } = useHomeOSState();
  const [charter] = usePersistentState("familyplan.charter", { charter: "", vision: "", mission: "" });
  const navigate = useNavigate();
  const { phase } = useLightPhase();
  const [editing, setEditing] = useState(null);

  const memberCount = members?.length || 0;
  const focal = memberCount === 0
    ? "Add the first family member to begin."
    : memberCount === 1
      ? "One resident, quietly recorded."
      : `${memberCount} residents, held together.`;

  const current = nowHHMM();
  const summaries = useMemo(() => {
    const map = {};
    for (const m of members || []) {
      const events = (dailyFamilySchedule || [])
        .filter(e => e.status !== "done" && e.startTime && (e.memberId === m.id || e.memberId === "all" || e.memberId === "everyone"))
        .sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
      const nextEvent = events.find(e => e.startTime >= current) || null;
      const outstanding = (tasks || []).filter(t =>
        (t.assignedTo === m.id || t.assignedTo === m.name) &&
        !["Done", "Completed", "Ara suggested", "Paused"].includes(t.status)).length;
      const activeGoal = (goals || []).find(g =>
        g.ownerType === "member" && g.ownerMemberId === m.id && g.status !== "complete") || null;
      map[m.id] = { nextEvent, outstanding, activeGoal };
    }
    return map;
  }, [members, tasks, goals, dailyFamilySchedule, current]);

  const sharedGoals = useMemo(
    () => (goals || []).filter(g => g.ownerType !== "member"),
    [goals]
  );
  const charterText = [charter?.charter, charter?.vision, charter?.mission].filter(v => v && v.trim());

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="family" />} phase={phase} testid="module-page-family">
      <PageHeading
        testid="family-hero"
        module="family"
        art="family"
        room="Family"
        title="The Family Room"
        focal={focal}
        actions={
          <PillButton
            variant="solid"
            onClick={() => setEditing({ memberId: null, mode: "add" })}
            testid="add-family-member-btn"
          >
            Add family member
          </PillButton>
        }
      />

      {/* Preserved legacy testids */}
      <div className="hidden" data-testid="module-title" aria-hidden>Family</div>
      <div className="hidden" data-testid="module-subtitle" aria-hidden>Profiles, routines, responsibilities and support needs for everyone in the household.</div>
      <div className="hidden" data-testid="module-actions" aria-hidden>
        <Link to="/home-os/setup" />
        <Link to="/home-os/command-centre" />
      </div>

      <EditorialSection
        testid="family-profiles"
        moduleMark={{ module: "family", label: "Everyone in your household" }}
        title="The people"
      >
        {memberCount === 0 ? (
          <div data-testid="family-empty">
            <p
              className="italic text-[17px] sm:text-[19px] leading-[1.4]"
              style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink)", fontWeight: 400, maxWidth: "32ch" }}
            >
              A residence begins with its people. Start with the people who make this house home.
            </p>
            <div className="mt-6">
              <PillButton
                variant="solid"
                onClick={() => setEditing({ memberId: null, mode: "add" })}
                testid="family-add-first-btn"
              >
                Add the first member
              </PillButton>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-x-10" data-testid="family-member-grid">
            {members.map((member) => (
              <div key={member.id} className="border-b" style={{ borderColor: "var(--ara-line-soft)" }}>
                <MemberCard
                  member={member}
                  nextEvent={summaries[member.id]?.nextEvent}
                  outstanding={summaries[member.id]?.outstanding || 0}
                  activeGoal={summaries[member.id]?.activeGoal}
                  onOpen={() => navigate(`/home-os/family/${member.id}`)}
                />
              </div>
            ))}
          </div>
        )}
      </EditorialSection>

      {sharedGoals.length > 0 && (
        <EditorialSection
          testid="family-shared-goals"
          moduleMark={{ module: "family", label: "Together" }}
          title="Shared family goals"
          aside={<TextLink onClick={() => navigate("/home-os/family-plan")} testid="family-open-planning">Open Planning →</TextLink>}
        >
          {sharedGoals.map((g, i) => (
            <div key={g.id} className={`flex items-baseline justify-between gap-4 py-3 ${i === sharedGoals.length - 1 ? "" : "border-b"}`} style={{ borderColor: "var(--ara-line-soft)" }} data-testid={`family-shared-goal-${g.id}`}>
              <p className="text-[14.5px]" style={inkRow}>{g.title || "Untitled goal"}</p>
              <p className="caption-ara flex-shrink-0" style={softText}>{GOAL_STATUS[g.status] || "Not started"}</p>
            </div>
          ))}
        </EditorialSection>
      )}

      <EditorialSection
        testid="family-charter"
        moduleMark={{ module: "family", label: "Who we are" }}
        title="Family charter"
        aside={<TextLink onClick={() => navigate("/home-os/family-plan")} testid="family-edit-charter">Edit in Planning →</TextLink>}
      >
        {charterText.length === 0 ? (
          <p className="prose-ara text-[14.5px] italic" style={softText} data-testid="family-charter-empty">
            The family's charter hasn't been written yet. It lives in Planning when you're ready.
          </p>
        ) : (
          <div data-testid="family-charter-text">
            {charterText.map((line, i) => (
              <p key={i} className={`italic text-[16px] leading-[1.55] ${i === 0 ? "" : "mt-3"}`} style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink)", fontWeight: 400, maxWidth: "48ch" }}>
                {line}
              </p>
            ))}
          </div>
        )}
      </EditorialSection>

      <Colophon>ARA · Family</Colophon>

      <FamilyMemberEditModal
        open={!!editing}
        memberId={editing?.memberId}
        mode={editing?.mode || "edit"}
        onClose={() => setEditing(null)}
      />
      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
